import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, API } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { StatCard } from "@/components/common/StatCard";
import { ScoreGauge } from "@/components/common/ScoreGauge";
import { ProgressRing } from "@/components/common/Progress";
import { RagBadge } from "@/components/common/RagBadge";
import { Button } from "@/components/ui/button";
import { fmtDate, fmtValue, RAG } from "@/lib/format";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from "recharts";

const REPORT_TYPES = [
  { key: "exec", label: "Executive Business Summary", icon: "FileText" },
  { key: "health", label: "Business Health Assessment", icon: "Activity" },
  { key: "plan", label: "My Plan Progress", icon: "Target" },
  { key: "kpi", label: "KPI Performance", icon: "Gauge" },
  { key: "actions", label: "Actions & Accountability", icon: "ListChecks" },
  { key: "coaching", label: "Coaching History", icon: "Users" },
  { key: "issues", label: "Issues & Decisions", icon: "TriangleAlert" },
  { key: "checkins", label: "Weekly Check-in", icon: "CalendarCheck" },
];

export default function Reports() {
  const { orgId } = useOrg();
  const [data, setData] = useState(null);

  useEffect(() => { if (orgId) { setData(null); api.get(`/reports/summary?org_id=${orgId}`).then((r) => setData(r.data)); } }, [orgId]);

  if (!data) return <PageLoading />;

  const kpiChart = (data.kpis || []).slice(0, 8).map((k) => ({ name: k.name, current: k.current_value, target: k.target, status: k.status }));

  const exportExcel = async () => {
    try {
      const token = localStorage.getItem("amden_token");
      const res = await fetch(`${API}/reports/export.xlsx?org_id=${orgId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Amden_Report_${(data.organisation.company_name || "client").replace(/\s+/g, "_")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel report downloaded");
    } catch (e) {
      toast.error("Couldn't export the Excel file.");
    }
  };

  const exportPdf = () => {
    const esc = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const kpiRows = (data.kpis || []).map((k) => `<tr><td>${esc(k.name)}</td><td>${esc(k.category)}</td><td>${esc(k.current_value)}</td><td>${esc(k.target)}</td><td>${esc(k.status)}</td></tr>`).join("");
    const prioRows = (data.plan.priorities || []).map((p) => `<tr><td>${esc(p.title)}</td><td>${esc(p.business_area)}</td><td>${esc(p.percent_complete)}%</td><td>${esc(p.status)}</td></tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Amden Report — ${esc(data.organisation.company_name)}</title>
      <style>
        *{box-sizing:border-box} body{font-family:Georgia,'Times New Roman',serif;color:#1E2D44;margin:0;padding:44px 52px;line-height:1.5}
        .hd{border-bottom:3px solid #FFB000;padding-bottom:16px;margin-bottom:26px}
        .ey{color:#B87F1E;font-size:11px;letter-spacing:.14em;text-transform:uppercase;font-family:Arial,sans-serif;font-weight:700}
        h1{font-family:Arial,sans-serif;color:#0B1729;font-size:26px;margin:6px 0 2px} .sub{color:#5b6a82;font-size:13px}
        h2{font-family:Arial,sans-serif;color:#0B1729;font-size:15px;background:#F4F6F9;border-left:3px solid #FFB000;padding:8px 12px;margin:24px 0 12px}
        .kpis{display:flex;gap:14px;flex-wrap:wrap;margin:8px 0}
        .m{flex:1;min-width:130px;border:1px solid #E4E8EF;border-radius:10px;padding:12px}
        .ml{font-family:Arial,sans-serif;font-size:11px;color:#5b6a82;text-transform:uppercase;letter-spacing:.08em}
        .mv{font-family:Arial,sans-serif;font-size:24px;font-weight:700;color:#0B1729;margin-top:2px}
        table{width:100%;border-collapse:collapse;font-size:12px;margin-top:6px}
        th{font-family:Arial,sans-serif;text-align:left;background:#0B1729;color:#fff;padding:7px 9px;font-size:11px}
        td{padding:6px 9px;border-bottom:1px solid #EEF1F6}
        @media print{body{padding:22px}}
      </style></head><body>
      <div class="hd"><div class="ey">Amden Compass · Executive Report</div>
      <h1>${esc(data.organisation.company_name)}</h1>
      <div class="sub">Generated ${new Date().toLocaleDateString("en-GB")}</div></div>
      <div class="kpis">
        <div class="m"><div class="ml">Health score</div><div class="mv">${data.health ? esc(data.health.overall) + "/100" : "—"}</div></div>
        <div class="m"><div class="ml">Plan progress</div><div class="mv">${esc(data.plan.percent)}%</div></div>
        <div class="m"><div class="ml">Action completion</div><div class="mv">${esc(data.actions.completion_rate)}%</div></div>
        <div class="m"><div class="ml">Overdue actions</div><div class="mv">${esc(data.actions.overdue)}</div></div>
        <div class="m"><div class="ml">Avg week rating</div><div class="mv">${esc(data.checkins.avg_score)}/10</div></div>
      </div>
      ${prioRows ? `<h2>My Plan — ${esc(data.plan.title || "")}</h2><table><thead><tr><th>Priority</th><th>Business area</th><th>Progress</th><th>Status</th></tr></thead><tbody>${prioRows}</tbody></table>` : ""}
      ${kpiRows ? `<h2>KPI Performance</h2><table><thead><tr><th>KPI</th><th>Category</th><th>Current</th><th>Target</th><th>Status</th></tr></thead><tbody>${kpiRows}</tbody></table>` : ""}
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to export the PDF."); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 350);
  };

  return (
    <div data-testid="reports-page">
      <PageHeader eyebrow="Reporting" title="Report Centre" subtitle="Executive summaries and performance reports, ready to export or present." icon="BarChart3"
        actions={<div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportExcel} className="border-[#E4E8EF]" data-testid="export-excel-btn"><Icons.Sheet size={16} className="mr-1.5" /> Excel</Button>
          <Button variant="outline" onClick={exportPdf} className="border-[#E4E8EF]" data-testid="export-pdf-btn"><Icons.FileDown size={16} className="mr-1.5" /> PDF</Button>
        </div>} />

      {/* Executive summary */}
      <div className="amden-card mb-6 p-6 lg:p-8">
        <div className="mb-5 flex items-center justify-between border-b border-[#EEF1F6] pb-4">
          <div>
            <div className="label-eyebrow mb-1">Executive summary</div>
            <h2 className="font-display text-xl font-bold">{data.organisation.company_name}</h2>
            <div className="text-sm text-[#1E2D44]/55">Generated {fmtDate(data.generated_at)}</div>
          </div>
          {data.health && <ScoreGauge score={data.health.overall} size={120} />}
        </div>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div className="rounded-xl bg-[#F4F6F9] p-4"><div className="label-eyebrow mb-1">Plan progress</div><div className="flex items-center gap-2"><ProgressRing value={data.plan.percent} size={44} stroke={5} label={`${data.plan.percent}%`} /><span className="text-sm text-[#1E2D44]/60">{data.plan.title || "No plan"}</span></div></div>
          <div className="rounded-xl bg-[#F4F6F9] p-4"><div className="label-eyebrow mb-1">Action completion</div><div className="font-display text-2xl font-bold">{data.actions.completion_rate}%</div><div className="text-xs text-[#1E2D44]/50">{data.actions.completed}/{data.actions.total} completed</div></div>
          <div className="rounded-xl bg-[#F4F6F9] p-4"><div className="label-eyebrow mb-1">Overdue actions</div><div className="font-display text-2xl font-bold text-[#C95555]">{data.actions.overdue}</div></div>
          <div className="rounded-xl bg-[#F4F6F9] p-4"><div className="label-eyebrow mb-1">Avg week rating</div><div className="font-display text-2xl font-bold">{data.checkins.avg_score}<span className="text-sm text-[#1E2D44]/40">/10</span></div></div>
        </div>
      </div>

      {/* KPI chart */}
      {kpiChart.length > 0 && (
        <div className="amden-card mb-6 p-6">
          <h3 className="mb-4 font-display text-base font-bold">KPI performance — current vs target</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={kpiChart} margin={{ left: 0, right: 10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F6" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#8A97AB" }} angle={-25} textAnchor="end" height={60} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#8A97AB" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E4E8EF", fontSize: 12 }} cursor={{ fill: "#F4F6F9" }} />
                <Bar dataKey="target" fill="#E4E8EF" radius={[4, 4, 0, 0]} name="Target" />
                <Bar dataKey="current" radius={[4, 4, 0, 0]} name="Current">
                  {kpiChart.map((k, i) => <Cell key={i} fill={RAG[k.status]?.solid || "#FFB000"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Report types */}
      <h3 className="mb-3 font-display text-base font-bold">Available reports</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {REPORT_TYPES.map((r) => {
          const Icon = Icons[r.icon];
          return (
            <div key={r.key} className="amden-card amden-card-hover flex items-center justify-between p-5" data-testid={`report-${r.key}`}>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0B1729] text-[#FFC24D]"><Icon size={18} /></span>
                <span className="text-sm font-semibold text-[#0B1729]">{r.label}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={exportPdf} className="text-[#FFB000] hover:bg-[#F4F6F9]"><Icons.Download size={15} /></Button>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-xs text-[#1E2D44]/45">Export the full report as a branded PDF or an Excel workbook using the buttons above.</p>
    </div>
  );
}
